import {
  AccountInfoField,
  SIGNUP_STATES,
  accountInfoErrors,
  formatAddress,
  locationFields,
} from "./accountInfo";
import {
  DetailRows,
  EditButton,
  FieldGroup,
  FormSection,
  NarrowField,
  ReadOnlyRow,
} from "./layout";
import { SectionProps, useAccountInfoFields } from "./useAccountInfoFields";

import { CheckboxItem } from "../../design-system/checkbox-item";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputField } from "../../design-system/input-field";
import { PracticeLocation } from "../npiLookup";
import { RadioGridSelect } from "../../design-system/radio-grid-select";
import { RadioItem } from "../../design-system/radio-item";
import { SelectField } from "../../design-system/select-field";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useFacilityTypes } from "../useFacilityTypes";
import { useState } from "react";

/** "+ Add secondary fax number", under or beside the facility fax. */
const AddSecondaryFaxButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="tw-flex tw-shrink-0 tw-items-center tw-gap-[4px] tw-self-end tw-border-0 tw-bg-transparent tw-p-0 tw-font-sohne tw-text-[14px] tw-text-verse-text-tertiary"
  >
    <FontAwesomeIcon
      icon={faPlus}
      aria-hidden="true"
      className="tw-h-[16px] tw-w-[16px]"
    />
    Add secondary fax number
  </button>
);

// Filled in from NPPES; any one missing opens the card for editing.
const PREFILLED_FIELDS: AccountInfoField[] = [
  "address1",
  "city",
  "stateAbbr",
  "zipcode",
  "facilityPhone",
  "facilityFax",
];

/** The practice locations NPPES lists, to pick the one the account is for. */
const LocationPicker = ({
  locations,
  onPick,
}: {
  locations: PracticeLocation[];
  onPick: (location: PracticeLocation) => void;
}) => (
  <FieldGroup>
    <p className="tw-m-0 tw-font-sohne tw-leading-[1.4]">
      <span className="tw-block tw-text-[12px] tw-text-verse-text-tertiary">
        We found multiple practice locations associated with your profile.
      </span>
      <span className="tw-block tw-text-[16px] tw-text-verse-text-primary">
        Please select the practice location you’d like to create your account
        for:
      </span>
    </p>
    <div
      role="radiogroup"
      aria-label="Practice location"
      // Shows about four and a half; the cut-off one hints at more.
      className="tw-flex tw-max-h-[250px] tw-flex-col tw-gap-[8px] tw-overflow-y-auto"
    >
      {locations.map((location, index) => (
        <RadioItem
          key={index}
          id={`signup-practice-location-${index}`}
          name="signup-practice-location"
          checked={false}
          onChange={() => onPick(location)}
        >
          {formatAddress(locationFields(location))}
        </RadioItem>
      ))}
    </div>
  </FieldGroup>
);

/** The facility's address, numbers, name and type. A prefilled address and
 * numbers start read-only; with several locations, the user picks one first. */
export const FacilityInfoSection = ({
  prefilled = false,
  practiceLocations = [],
  form,
  updateForm,
  serverErrors,
}: SectionProps & {
  prefilled?: boolean;
  practiceLocations?: PracticeLocation[];
}) => {
  const { errors, fieldProps, textField } = useAccountInfoFields({
    form,
    updateForm,
    serverErrors,
  });
  const facilityTypes = useFacilityTypes();
  // Editable from the start if NPPES left a required field empty or invalid.
  const [editing, setEditing] = useState(
    !prefilled || PREFILLED_FIELDS.some((field) => errors[field]),
  );
  // "Same as facility fax" keeps the medical records fax in sync until it's edited.
  const [faxLinked, setFaxLinked] = useState(
    form.medicalRecordsFax !== "" &&
      form.medicalRecordsFax === form.facilityFax,
  );
  const [showSecondaryFax, setShowSecondaryFax] = useState(
    form.secondaryFacilityFax !== "",
  );
  // Several locations and none picked yet: the picker is all the card shows.
  const [picking, setPicking] = useState(
    practiceLocations.length > 1 && !form.address1,
  );
  const pickLocation = (location: PracticeLocation) => {
    const fields = locationFields(location);
    updateForm(fields);
    setPicking(false);
    // As with a single location: editable if NPPES left something out.
    const pickedErrors = accountInfoErrors({ ...form, ...fields });
    setEditing(PREFILLED_FIELDS.some((field) => pickedErrors[field]));
  };
  const addSecondaryFax = !showSecondaryFax && (
    <AddSecondaryFaxButton onClick={() => setShowSecondaryFax(true)} />
  );

  const contactRows = editing
    ? [
        <InputField
          key="address1"
          inline
          {...textField("address1")}
          label="Facility Address"
          autoComplete="address-line1"
        />,
        <InputField
          key="address2"
          inline
          id="signup-address2"
          label="Address Line 2 (optional)"
          value={form.address2}
          onChange={(address2) => updateForm({ address2 })}
          autoComplete="address-line2"
        />,
        <InputField
          key="city"
          inline
          {...textField("city")}
          label="City"
          autoComplete="address-level2"
        />,
        <SelectField
          key="stateAbbr"
          inline
          {...fieldProps("stateAbbr")}
          label="State"
          value={form.stateAbbr}
          onChange={(stateAbbr) => updateForm({ stateAbbr })}
          options={SIGNUP_STATES.map((state) => ({
            value: state,
            label: state,
          }))}
          autoComplete="address-level1"
        />,
        <InputField
          key="zipcode"
          inline
          {...textField("zipcode")}
          label="Zip Code"
          inputMode="numeric"
          autoComplete="postal-code"
        />,
        <InputField
          key="facilityPhone"
          inline
          {...textField("facilityPhone")}
          label="Facility Phone Number"
          type="tel"
          inputMode="tel"
        />,
        <div
          key="facilityFax"
          className="tw-flex tw-w-full tw-flex-col tw-gap-[8px]"
        >
          <InputField
            inline
            {...fieldProps("facilityFax")}
            label="Facility Fax Number"
            type="tel"
            inputMode="tel"
            value={form.facilityFax}
            onChange={(facilityFax) =>
              updateForm({
                facilityFax,
                ...(faxLinked && { medicalRecordsFax: facilityFax }),
              })
            }
          />
          {addSecondaryFax}
        </div>,
      ]
    : [
        <ReadOnlyRow
          key="address"
          label="Facility Address"
          value={formatAddress(form)}
        />,
        <ReadOnlyRow
          key="facilityPhone"
          label="Facility Phone Number"
          value={form.facilityPhone}
        />,
        <ReadOnlyRow
          key="facilityFax"
          label="Facility Fax Number"
          value={form.facilityFax}
        >
          {addSecondaryFax}
        </ReadOnlyRow>,
      ];

  if (picking) {
    return (
      <FormSection title="Facility Information">
        <LocationPicker locations={practiceLocations} onPick={pickLocation} />
      </FormSection>
    );
  }

  return (
    <FormSection
      title="Facility Information"
      action={
        !editing && (
          <EditButton
            section="facility information"
            onClick={() => setEditing(true)}
          />
        )
      }
    >
      <DetailRows>
        {contactRows}
        {showSecondaryFax && (
          <InputField
            inline
            {...textField("secondaryFacilityFax")}
            label="Secondary Fax Number (optional)"
            type="tel"
            inputMode="tel"
          />
        )}
      </DetailRows>

      {/* NPPES doesn't have these, so they're always typed in. */}
      <FieldGroup>
        <InputField
          {...textField("facilityName")}
          label="Your facility's full exact name:"
          placeholder="e.g. University Hospitals Seidman Cancer Center"
          autoComplete="organization"
        />
        <NarrowField>
          <RadioGridSelect
            {...fieldProps("facilityTypeId")}
            label="Select Facility Type"
            value={form.facilityTypeId?.toString() ?? ""}
            onChange={(id) => updateForm({ facilityTypeId: Number(id) })}
            options={facilityTypes.map((type) => ({
              value: type.id.toString(),
              label: type.name,
            }))}
          />
        </NarrowField>
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <InputField
            {...fieldProps("medicalRecordsFax")}
            label="Fax number of your medical record department (optional)"
            inputClassName="tw-w-[240px] tw-max-w-full"
            type="tel"
            inputMode="tel"
            value={form.medicalRecordsFax}
            onChange={(medicalRecordsFax) => {
              setFaxLinked(false);
              updateForm({ medicalRecordsFax });
            }}
          />
          <CheckboxItem
            id="signup-medical-records-fax-linked"
            bordered={false}
            checked={faxLinked}
            onChange={(checked) => {
              setFaxLinked(checked);
              if (checked) {
                updateForm({ medicalRecordsFax: form.facilityFax });
              }
            }}
          >
            Same as facility fax number
          </CheckboxItem>
        </div>
      </FieldGroup>
    </FormSection>
  );
};
